import { useState, useEffect } from 'react';
import { Heart, MessageCircle, Lock, Send, Image, Pin, Crown, Trophy, Loader2, Plus, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

// ===== MEMBERSHIP TIERS DISPLAY =====
export const TierCards = ({ hubId, accentColor, onSubscribe }) => {
  const [tiers, setTiers] = useState([]);
  useEffect(() => { api.get(`/community/tiers/${hubId}`).then(r => setTiers(r.data.tiers || [])).catch(() => {}); }, [hubId]);
  if (tiers.length === 0) return null;

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-bold">Membership</h3>
      {tiers.map(tier => (
        <Card key={tier.id} className="bg-slate-800/50 border-slate-700 hover:border-indigo-500 transition-all">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {tier.badge_emoji && <span className="text-lg">{tier.badge_emoji}</span>}
                <h4 className="font-bold">{tier.name}</h4>
              </div>
              <span className="font-bold" style={{ color: accentColor }}>{tier.price_monthly > 0 ? `$${tier.price_monthly}/mo` : 'Free'}</span>
            </div>
            {tier.description && <p className="text-sm text-slate-400 mb-3">{tier.description}</p>}
            {tier.perks?.length > 0 && (
              <ul className="text-xs text-slate-400 space-y-1 mb-3">{tier.perks.map((p, i) => <li key={i} className="flex items-center gap-1"><Crown className="w-3 h-3" style={{ color: tier.badge_color }} /> {p}</li>)}</ul>
            )}
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">{tier.subscriber_count || 0} members</span>
              <Button size="sm" style={{ backgroundColor: accentColor }} className="text-white" onClick={() => onSubscribe(tier)}>{tier.price_monthly > 0 ? 'Subscribe' : 'Join Free'}</Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ===== COMMUNITY FEED =====
export const CommunityFeed = ({ hubId, accentColor, isCreator }) => {
  const { isAuthenticated, user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [postType, setPostType] = useState('update');
  const [posting, setPosting] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentTexts, setCommentTexts] = useState({});
  const [comments, setComments] = useState({});

  useEffect(() => { fetchPosts(); }, [hubId]);

  const fetchPosts = async () => {
    try { const res = await api.get(`/community/posts/${hubId}`); setPosts(res.data.posts || []); } catch {} finally { setLoading(false); }
  };

  const handlePost = async () => {
    if (!newPost.trim()) return;
    setPosting(true);
    try {
      await api.post('/community/posts', { hub_id: hubId, content: newPost, post_type: postType, min_tier: 'free' });
      setNewPost('');
      fetchPosts();
      toast.success('Posted!');
    } catch { toast.error('Failed'); }
    finally { setPosting(false); }
  };

  const handleLike = async (postId) => {
    if (!isAuthenticated) { toast.info('Sign in to like'); return; }
    try {
      const res = await api.post(`/community/posts/${postId}/like`);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: (p.likes_count || 0) + (res.data.status === 'liked' ? 1 : -1) } : p));
    } catch {}
  };

  const toggleComments = async (postId) => {
    if (expandedComments[postId]) {
      setExpandedComments(prev => ({ ...prev, [postId]: false }));
      return;
    }
    try {
      const res = await api.get(`/community/posts/${postId}/comments`);
      setComments(prev => ({ ...prev, [postId]: res.data.comments || [] }));
    } catch {}
    setExpandedComments(prev => ({ ...prev, [postId]: true }));
  };

  const handleComment = async (postId) => {
    const text = commentTexts[postId];
    if (!text?.trim() || !isAuthenticated) return;
    try {
      const res = await api.post(`/community/posts/${postId}/comment`, { content: text });
      setComments(prev => ({ ...prev, [postId]: [...(prev[postId] || []), res.data] }));
      setCommentTexts(prev => ({ ...prev, [postId]: '' }));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments_count: (p.comments_count || 0) + 1 } : p));
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">Community</h3>
        <span className="text-xs text-slate-500">{posts.length} posts</span>
      </div>

      {/* New Post (creator only) */}
      {isCreator && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 space-y-3">
            <Textarea value={newPost} onChange={e => setNewPost(e.target.value)} rows={2} placeholder="Share with your community..." className="bg-transparent border-slate-600 text-white" />
            <div className="flex justify-between">
              <div className="flex gap-2">
                {['update', 'announcement', 'media_drop', 'event_recap'].map(t => (
                  <Badge key={t} variant={postType === t ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => setPostType(t)}>{t.replace('_', ' ')}</Badge>
                ))}
              </div>
              <Button size="sm" style={{ backgroundColor: accentColor }} className="text-white" onClick={handlePost} disabled={posting || !newPost.trim()}>
                {posting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Post
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Posts */}
      {loading ? <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-500 mx-auto" /></div> : posts.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700 p-8 text-center text-slate-500">No posts yet</Card>
      ) : posts.map(post => (
        <Card key={post.id} className="bg-slate-800/50 border-slate-700 relative overflow-hidden">
          {post.locked && (
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/60 to-transparent z-10 flex items-end justify-center pb-6">
              <div className="text-center"><Lock className="w-6 h-6 text-slate-400 mx-auto mb-2" /><p className="text-sm text-slate-400">Upgrade to unlock</p></div>
            </div>
          )}
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: `${accentColor}33`, color: accentColor }}>{post.author_name?.charAt(0) || '?'}</div>
              <div className="flex-1">
                <span className="font-medium text-sm text-white">{post.author_name}</span>
                <span className="text-xs text-slate-500 ml-2">{new Date(post.created_at).toLocaleDateString()}</span>
              </div>
              {post.pinned && <Pin className="w-4 h-4 text-amber-400" />}
              <Badge variant="outline" className="text-[10px] text-slate-500">{post.post_type?.replace('_', ' ')}</Badge>
            </div>
            <p className="text-slate-300 text-sm whitespace-pre-wrap mb-3">{post.content}</p>
            {post.media?.length > 0 && (
              <div className="grid grid-cols-2 gap-2 mb-3">{post.media.map((m, i) => <img key={i} src={m.url} alt="" className="rounded-lg w-full aspect-video object-cover" />)}</div>
            )}
            <div className="flex items-center gap-4 text-slate-500 text-sm">
              <button className="flex items-center gap-1 hover:text-red-400 transition-colors" onClick={() => handleLike(post.id)}><Heart className="w-4 h-4" /> {post.likes_count || 0}</button>
              <button className="flex items-center gap-1 hover:text-indigo-400 transition-colors" onClick={() => toggleComments(post.id)}><MessageCircle className="w-4 h-4" /> {post.comments_count || 0}</button>
            </div>
            {/* Comments */}
            {expandedComments[post.id] && (
              <div className="mt-3 pt-3 border-t border-slate-700 space-y-2">
                {(comments[post.id] || []).map(c => (
                  <div key={c.id} className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-[10px] font-bold text-slate-400 shrink-0">{c.author_name?.charAt(0)}</div>
                    <div><span className="text-xs font-medium text-slate-300">{c.author_name}</span><p className="text-xs text-slate-400">{c.content}</p></div>
                  </div>
                ))}
                {isAuthenticated && (
                  <div className="flex gap-2">
                    <Input value={commentTexts[post.id] || ''} onChange={e => setCommentTexts(prev => ({ ...prev, [post.id]: e.target.value }))} placeholder="Reply..." className="h-8 text-xs bg-transparent border-slate-600 text-white" onKeyDown={e => { if (e.key === 'Enter') handleComment(post.id); }} />
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => handleComment(post.id)}><Send className="w-3 h-3" /></Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ===== LEADERBOARD =====
export const FanLeaderboard = ({ hubId, accentColor }) => {
  const [leaders, setLeaders] = useState([]);
  useEffect(() => { api.get(`/community/leaderboard/${hubId}`).then(r => setLeaders(r.data.leaderboard || [])).catch(() => {}); }, [hubId]);
  if (leaders.length === 0) return null;

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-bold flex items-center gap-2"><Trophy className="w-5 h-5" style={{ color: accentColor }} /> Fan Leaderboard</h3>
      {leaders.slice(0, 10).map(fan => (
        <div key={fan.user_id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/30">
          <span className="text-lg font-bold text-slate-500 w-6 text-center">#{fan.rank}</span>
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm" style={{ backgroundColor: fan.badge_color + '33', color: fan.badge_color }}>{fan.badge_emoji}</div>
          <div className="flex-1">
            <p className="font-medium text-sm text-white">{fan.user_name}</p>
            <p className="text-[10px] text-slate-500">{fan.events_attended} events · {fan.badge_name}</p>
          </div>
        </div>
      ))}
    </div>
  );
};
